/**
 * Helpers for treating generation parameters (temperature, top_p) as plain
 * entries in an inference-options override's `options` map. An override carries inference options
 * only (provider-facing, snake_case) under `.options`; the model, provider, and credentials always
 * come from the selected AI Profile and can no longer be overridden. The agent and the judge override
 * in exactly the same way, and every generation parameter is simply an option rather than a separate
 * typed field.
 */

export type InferenceOverride = Record<string, any>;

/**
 * The effective, resolved generation parameters for a stored benchmark config/run. Every generation
 * option is read (snake_case) from the run's inference-options override; legacy top-level camelCase
 * fields on older stored configs are used as a fallback so historic runs still render.
 */
export type EffectiveInferenceParams = {
  model?: string;
  temperature?: number;
  top_p?: number;
};

/**
 * Read a numeric option from an inference-options override, or null when it is not set.
 */
export const getOverrideOption = (
  override: InferenceOverride | null | undefined,
  key: string,
): number | null => {
  const value = override?.options?.[key];
  return typeof value === "number" ? value : null;
};

/**
 * Return a new inference-options override with `key` set to `value` in its options (or removed when
 * value is null). Returns null when the resulting override carries nothing, so an emptied override
 * clears cleanly.
 */
export const setOverrideOption = (
  override: InferenceOverride | null | undefined,
  key: string,
  value: number | null,
): InferenceOverride | null => {
  const next: InferenceOverride = { ...(override ?? {}) };
  const options: Record<string, any> = { ...(next.options ?? {}) };

  if (value === null) {
    delete options[key];
  } else {
    options[key] = value;
  }

  if (Object.keys(options).length > 0) {
    next.options = options;
  } else {
    delete next.options;
  }

  return Object.keys(next).length > 0 ? next : null;
};

/**
 * Derive the effective generation parameters for a stored benchmark config's `parameters` block.
 * Reads model + snake_case options from the inference-options override, falling back to legacy
 * top-level fields.
 */
export const effectiveInferenceParams = (
  params: Record<string, any> | null | undefined,
): EffectiveInferenceParams => {
  const p = params ?? {};
  const opts: Record<string, any> = p.override?.options ?? {};

  const pick = (snake: string, camel: string): number | undefined => {
    const value = opts[snake] ?? p[camel];
    return typeof value === "number" ? value : undefined;
  };

  return {
    model: p.model ?? undefined,
    temperature: pick("temperature", "temperature"),
    top_p: pick("top_p", "topP"),
  };
};
